import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Users, Search, MoreHorizontal, MoreVertical, Share2, Pencil, Trash2, ArrowUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import useStore from '@/store/useStore';
import { EntityActions } from '@/components/crud/EntityActions';
import { toast } from '@/components/ui/use-toast';
import ViewToggle from '@/components/common/ViewToggle';
import BulkImport from '@/components/common/BulkImport';
import SortableTableHeader from '@/components/common/SortableTableHeader';
import UniqueIdDisplay from '@/components/common/UniqueIdDisplay';
import BulkActions from '@/components/common/BulkActions';
import { Team, Player } from '@/types/cricket';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { getRandomColor } from '@/store/useStore';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const TeamsPage = () => {
  const { teams, tournaments, fetchTeams, fetchTournaments, updateTeam, deleteTeam, addTeam } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTournamentId, setFilterTournamentId] = useState<string>('all');
  const [view, setView] = useState<'card' | 'list'>('card');
  const [sortKey, setSortKey] = useState<string | null>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [filterPlayerCount, setFilterPlayerCount] = useState<string>('all');

  useEffect(() => {
    fetchTeams();
    fetchTournaments();
  }, [fetchTeams, fetchTournaments]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const tournamentFilterOptions = React.useMemo(() => {
    const options: { id: string; name: string }[] = [];

    options.push({ id: 'all', name: 'All Tournaments' });

    tournaments.forEach(tournament => {
      options.push({ id: tournament.id, name: tournament.name });
    });

    const distinctOptions = Array.from(new Map(options.map(item => [item.id, item])).values());
    return distinctOptions.sort((a, b) => {
      if (a.id === 'all') return -1;
      if (b.id === 'all') return 1;
      return a.name.localeCompare(b.name, 'en-US', { sensitivity: 'base' })
    });
  }, [tournaments]);

  const playerCountOptions = React.useMemo(() => {
    const counts = Array.from(new Set(teams.map(team => team.players.length)));
    counts.sort((a, b) => a - b);
    return ['all', ...counts];
  }, [teams]);

  const sortedTeams = React.useMemo(() => {
    const filtered = teams.filter(team => {
      const teamName = team.name.toLowerCase();
      const teamShortCode = team.shortCode?.toLowerCase() || '';
      const teamUniqueId = team.uniqueId?.toLowerCase() || '';
      const tournamentName = tournaments.find(t => t.id === team.tournamentId)?.name?.toLowerCase() || '';

      // Include player names in the search
      const playerNames = team.players.map(p => p.name.toLowerCase()).join(' ');

      const searchLower = searchTerm.toLowerCase();
      const textMatches =
        teamName.includes(searchLower) ||
        teamShortCode.includes(searchLower) ||
        teamUniqueId.includes(searchLower) ||
        tournamentName.includes(searchLower) ||
        playerNames.includes(searchLower);

      if (searchTerm && !textMatches) return false;

      if (filterTournamentId !== 'all') {
        if (filterTournamentId === 'UNKNOWN_TOURNAMENT_NULL') {
          if (team.tournamentId === null || team.tournamentId === undefined || team.tournamentId === '') return true;
          return false;
        } else {
          return team.tournamentId === filterTournamentId;
        }
      }

      if (filterPlayerCount !== 'all' && team.players.length.toString() !== filterPlayerCount) {
        return false;
      }

      return true;
    });

    if (sortKey) {
      filtered.sort((a, b) => {
        let aValue: string | number | boolean | Date | undefined | Player[];
        let bValue: string | number | boolean | Date | undefined | Player[];
    
        if (sortKey === 'createdAt' || sortKey === 'updatedAt') {
          aValue = a[sortKey] ? new Date(a[sortKey] as string) : new Date(0);
          bValue = b[sortKey] ? new Date(b[sortKey] as string) : new Date(0);
          return sortDirection === 'asc' ? (aValue as Date).getTime() - (bValue as Date).getTime() : (bValue as Date).getTime() - (aValue as Date).getTime();
        } else if (sortKey === 'playersCount') {
          aValue = a.players.length;
          bValue = b.players.length;
        } else if (sortKey === 'tournamentId') {
          aValue = tournaments.find(t => t.id === a.tournamentId)?.name || '';
          bValue = tournaments.find(t => t.id === b.tournamentId)?.name || '';
        } else {
          aValue = a[sortKey as keyof Team];
          bValue = b[sortKey as keyof Team];
        }
    
        const comparableAValue: string | number = 
          (aValue === undefined || aValue === null) 
            ? (sortKey === 'playersCount' ? 0 : '') 
            : (Array.isArray(aValue) ? aValue.length : aValue) as string | number;
        const comparableBValue: string | number = 
          (bValue === undefined || bValue === null) 
            ? (sortKey === 'playersCount' ? 0 : '') 
            : (Array.isArray(bValue) ? bValue.length : bValue) as string | number;
    
        if (typeof comparableAValue === 'string' && typeof comparableBValue === 'string') {
            return sortDirection === 'asc' ? comparableAValue.localeCompare(comparableBValue, 'en-US', { sensitivity: 'base' }) : comparableBValue.localeCompare(comparableAValue, 'en-US', { sensitivity: 'base' });
        }
    
        if (typeof comparableAValue === 'number' && typeof comparableBValue === 'number') {
          return sortDirection === 'asc' ? comparableAValue - comparableBValue : comparableBValue - comparableAValue;
        }
    
        const finalAValue = String(comparableAValue);
        const finalBValue = String(comparableBValue);
    
        return sortDirection === 'asc' ? finalAValue.localeCompare(finalBValue, 'en-US', { sensitivity: 'base' }) : finalBValue.localeCompare(finalAValue, 'en-US', { sensitivity: 'base' });
      });
    }
    

    return filtered;
  }, [teams, searchTerm, sortKey, sortDirection, tournaments, filterTournamentId, filterPlayerCount]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTeams(sortedTeams.map(t => t.id));
    } else {
      setSelectedTeams([]);
    }
  };

  const handleSelectTeam = (teamId: string, checked: boolean) => {
    if (checked) {
      setSelectedTeams([...selectedTeams, teamId]);
    } else {
      setSelectedTeams(selectedTeams.filter(id => id !== teamId));
    }
  };

  const handleBulkDelete = async () => {
    for (const teamId of selectedTeams) {
      try {
        await deleteTeam(teamId);
      } catch (error) {
        console.error('Error deleting team:', error);
      }
    }
    setSelectedTeams([]);
  };

  const handleBulkEdit = () => {
    console.log('Bulk edit teams:', selectedTeams);
  };

  const hasTournaments = tournaments.length > 0;

  const handleBulkImport = async (data: { name: string; shortCode: string; color: string; tournamentId: string }[]) => {
    for (const row of data) {
      try {
        const tournament = tournaments.find(t => t.uniqueId === row.tournamentId);
        await addTeam({
          name: row.name || '',
          shortCode: row.shortCode || '',
          color: row.color || '#10b981',
          tournamentId: tournament?.id,
        });
      } catch (error) {
        console.error('Error importing team:', error);
      }
    }
  };

  const sampleTeamData = [
    { name: 'Mumbai Indians', shortCode: 'MI', color: '#004BA0', tournamentId: 'T-0XX001' }
  ];

  const csvHeaders = ['name', 'shortCode', 'color', 'tournamentId'];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Teams</h1>
          <p className="text-gray-500">Manage cricket teams and players</p>
        </div>
        <div className="flex gap-2">
          <BulkImport 
            type="teams"
            onImport={handleBulkImport}
            sampleData={sampleTeamData}
            headers={csvHeaders}
          />
          <Link to="/teams/new">
            <Button
              onClick={(e) => {
                if (!hasTournaments) {
                  e.preventDefault();
                  toast({
                    title: "Cannot create teams yet",
                    description: "Please create a tournament first.",
                    variant: "destructive",
                  });
                }
              }}
            >
              <Plus size={16} className="mr-2" />
              Add Team
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex gap-4 mb-6">
        <div className="flex-1 flex items-center gap-2">
          <div className="relative" style={{ maxWidth: '320px', width: '100%' }}>
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="text"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 pr-4"
            />
          </div>
          <Select value={filterTournamentId} onValueChange={setFilterTournamentId}>
            <SelectTrigger style={{ minWidth: '180px' }}>
              <SelectValue placeholder="Filter by tournament" />
            </SelectTrigger>
            <SelectContent>
              {tournamentFilterOptions.map(option => (
                <SelectItem key={option.id} value={option.id}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterPlayerCount} onValueChange={setFilterPlayerCount}>
            <SelectTrigger style={{ minWidth: '160px' }}>
              <SelectValue placeholder="Filter by players" />
            </SelectTrigger>
            <SelectContent>
              {playerCountOptions.map(count => (
                <SelectItem key={count} value={String(count)}>
                  {count === 'all' ? 'All Player Counts' : `${count} players`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-shrink-0 flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-full">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {['name', 'createdAt', 'updatedAt'].map(key => (
                  <DropdownMenuItem key={key} onSelect={() => handleSort(key)}>
                    <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    {sortKey === key && <ArrowUp className={`ml-auto h-4 w-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}>
                  Toggle Sort Direction
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

          <ViewToggle view={view} onViewChange={setView} />
        </div>
      </div>

      {selectedTeams.length > 0 && view === 'list' && (
        <BulkActions
          selectedCount={selectedTeams.length}
          onDelete={handleBulkDelete}
          onEdit={handleBulkEdit}
          entityType="team"
        />
      )}

      {sortedTeams.length > 0 ? (
        view === 'card' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedTeams.map((team) => (
              <Card key={team.id} className="hover:shadow-lg transition-shadow">
                <div className="h-2 rounded-t-lg" style={{ backgroundColor: team.color || '#10b981' }}></div>
                <CardHeader>
                  <div className="flex items-start justify-between flex-wrap min-w-0">
                    <div className="flex-1 flex items-start gap-4 min-w-0">
                      {team.logoUrl ? (
                        <img
                          src={team.logoUrl}
                          alt={team.name + ' logo'}
                          className="w-16 h-16 object-cover rounded-full border-2 border-gray-200 bg-white shadow-sm"
                        />
                      ) : (
                        <div
                          className="w-16 h-16 rounded-full border-2 border-gray-200 flex items-center justify-center shadow-sm"
                          style={{ backgroundColor: team.color || '#10b981' }}
                        >
                          <span className="text-1xl font-bold text-white drop-shadow-md select-none">
                            {team.shortCode?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col justify-center min-w-0">
                        <CardTitle className="text-xl mb-0">{team.name}</CardTitle>
                        {team.shortCode && (
                          <div className="w-fit">
                            <span className="inline-block text-sm font-bold bg-muted px-2 py-0.5 rounded-full mt-1">
                              {team.shortCode}
                            </span>
                          </div>
                        )}
                        {team.uniqueId && (
                          <UniqueIdDisplay uniqueId={team.uniqueId} className="mt-1" />
                        )}
                        <div className="mt-2">
                          <div className="flex items-center gap-1">
                            <Users size={14} />
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-pointer">{team.players.length} Players</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {team.players.length > 0 ? (
                                  <ul className="space-y-1 w-64">
                                    {team.players.map((player) => (
                                      <li
                                        key={player.id}
                                        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent transition-colors cursor-pointer"
                                      >
                                        {/* Avatar/Initials */}
                                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center font-bold text-xs text-primary">
                                          {player.avatarUrl ? (
                                            <img src={player.avatarUrl} alt={player.name} className="w-7 h-7 rounded-full object-cover" />
                                          ) : (
                                            player.name
                                              .split(' ')
                                              .map((n) => n[0])
                                              .join('')
                                              .toUpperCase()
                                          )}
                                        </div>
                                        {/* Name and Role */}
                                        <span className="font-medium truncate max-w-[110px]">{player.name}</span>
                                        <span
                                          className={`
                                            ml-2 text-xs px-2 py-0.5 rounded-full whitespace-nowrap
                                            ${player.role === 'bowler' ? 'bg-blue-100 text-blue-700' : ''}
                                            ${player.role === 'batsman' ? 'bg-green-100 text-green-700' : ''}
                                            ${player.role === 'all-rounder' ? 'bg-yellow-100 text-yellow-700' : ''}
                                            ${player.role?.includes('wicketkeeper') ? 'bg-purple-100 text-purple-700' : ''}
                                          `}
                                        >
                                          {player.role}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span>No players yet</span>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end self-start">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 rounded hover:bg-muted transition-colors" title="More actions">
                            <MoreVertical size={20} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              const url = `${window.location.origin}/teams/${team.id}`;
                              navigator.clipboard.writeText(url);
                              toast({ title: 'Team link copied!', description: 'Team URL copied to clipboard.' });
                            }}
                          >
                            <Share2 size={16} className="mr-2" /> Share
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => document.getElementById(`edit-team-${team.id}`)?.click()}
                          >
                            <Pencil size={16} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => deleteTeam(team.id)}
                            className="text-red-600"
                          >
                            <Trash2 size={16} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {/* Hidden EntityActions for edit dialog */}
                      <div style={{ display: 'none' }}>
                        <EntityActions
                          entity={team}
                          entityType="team"
                          onEdit={updateTeam}
                          onDelete={deleteTeam}
                          tournaments={tournaments}
                          triggerId={`edit-team-${team.id}`}
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="pt-4 border-t">
                    <Link to={`/teams/${team.id}`}>
                      <Button variant="default" size="sm" className="w-full">
                        View Team
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedTeams.length === sortedTeams.length && sortedTeams.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <SortableTableHeader
                    label="Name"
                    sortKey="name"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[200px]"
                  />
                  <SortableTableHeader
                    label="Short Code"
                    sortKey="shortCode"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[120px]"
                  />
                  <SortableTableHeader
                    label="Tournament"
                    sortKey="tournamentId"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[150px]"
                  />
                  <SortableTableHeader
                    label="Players"
                    sortKey="playersCount"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    label="Unique ID"
                    sortKey="uniqueId"
                    currentSort={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="min-w-[150px]"
                  />
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTeams.map((team) => (
                  <TableRow key={team.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedTeams.includes(team.id)}
                        onCheckedChange={(checked) => handleSelectTeam(team.id, checked as boolean)}
                      />
                    </TableCell>
                    <TableCell className="font-medium flex items-center gap-2">
                      {team.logoUrl ? (
                        <img
                          src={team.logoUrl}
                          alt={team.name + ' logo'}
                          className="w-8 h-8 object-cover rounded-full border border-gray-200 bg-white"
                        />
                      ) : (
                        <div
                          className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-white font-bold text-sm uppercase select-none"
                          style={{ backgroundColor: team.color || getRandomColor() }}
                        >
                          {team.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      <Link to={`/teams/${team.id}`} className="hover:underline flex items-center gap-2">
                        {team.name}
                        {team.color && (
                          <span
                            className="w-3 h-3 rounded-full inline-block ml-1 border border-gray-300"
                            style={{ backgroundColor: team.color }}
                            title={team.color}
                          />
                        )}
                      </Link>
                    </TableCell>
                    <TableCell>{team.shortCode}</TableCell>
                    <TableCell>{tournaments.find(t => t.id === team.tournamentId)?.name || 'N/A'}</TableCell>
                    <TableCell>{team.players.length}</TableCell>
                    <TableCell><UniqueIdDisplay uniqueId={team.uniqueId} /></TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Link to={`/teams/${team.id}/edit`}>
                          <Button variant="outline" size="sm">
                            View Details
                          </Button>
                        </Link>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded hover:bg-muted transition-colors" title="More actions">
                              <MoreVertical size={20} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                const url = `${window.location.origin}/teams/${team.id}`;
                                navigator.clipboard.writeText(url);
                                toast({ title: 'Team link copied!', description: 'Team URL copied to clipboard.' });
                              }}
                            >
                              <Share2 size={16} className="mr-2" /> Share
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => document.getElementById(`edit-team-${team.id}`)?.click()}
                            >
                              <Pencil size={16} className="mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => deleteTeam(team.id)}
                              className="text-red-600"
                            >
                              <Trash2 size={16} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )
      ) : (
        <div className="text-center py-12 bg-card rounded-lg">
          <h3 className="text-xl font-medium text-foreground">No teams yet</h3>
          {hasTournaments ? (
            <>
              <p className="text-muted-foreground mb-6">Add your first cricket team</p>
              <Link to="/teams/new">
                <Button className="bg-cricket-green">
                  <Plus size={16} className="mr-2" />
                  Add Team
                </Button>
              </Link>
            </>
          ) : (
            <>
              <p className="text-muted-foreground mb-6">You need to create a tournament before adding teams</p>
              <Link to="/tournaments/new">
                <Button className="bg-cricket-green">
                  <Plus size={16} className="mr-2" />
                  Create Tournament First
                </Button>
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamsPage;
